'use client';
import { useState, useRef, useEffect, useCallback, useSyncExternalStore } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { ChevronDown, LogOut, Loader2, Users, Mail, ChevronRight, Gift, Monitor } from '@/components/ui/icons';
import Link from 'next/link';
import { signOut } from '@/lib/auth';
import { useBillingInfo } from '@/hooks/useBillingInfo';
import { useMemberships } from '@/hooks/useMemberships';
import { usePendingInvitations } from '@/hooks/useTeams';
import { formatOlives } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { OliveIcon, DiscordIcon } from '@/components/icons';
import { TrackedDiscordLink } from '@/components/analytics/TrackedDiscordLink';
import { isDesktopApp } from '@/lib/desktopApp';
import { LocaleSwitcher } from '@/components/i18n/LocaleSwitcher';
import { trackAccountDropdownOpened, trackAccountDropdownBalanceClicked } from '@/lib/analytics';
import { PendingTeamInvitationsPanel } from './PendingTeamInvitationsPanel';
import dynamic from 'next/dynamic';
// Opened on demand; keeps the top-up panel off the editor's first paint.
const OliveBalancePopover = dynamic(()=>import('@/components/billing/OliveBalancePopover').then((m)=>m.OliveBalancePopover), {
    ssr: false
});
const triggerClass = 'flex items-center gap-2 h-8 px-2.5 text-sm rounded-md hover:bg-secondary/50 transition-colors';
const itemClass = 'w-full flex items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors';
interface AccountDropdownProps {
    /** User's display name */ alias: string;
    /** User's avatar color */ color: string;
    /** User's email address */ email: string | null;
    /** Whether the user is anonymous (guest) */ isAnonymous: boolean;
    /** Callback when guest clicks to sign up */ onSignUpClick: () => void;
    /**
   * Visual variant - 'default' has no border, 'outlined' has border, 'avatar'
   * is the initial-only circle, 'row' is the sidebar's full-width 36px row
   * (20px avatar + name + chevron) whose menu opens upward, 'rail' is the
   * collapsed sidebar's 32px avatar-only square, also opening upward.
   */ variant?: 'default' | 'outlined' | 'avatar' | 'row' | 'rail';
    /**
   * `full` (default) shows the complete menu. `focused` reduces it to
   * identity, the language switcher, and log out for the onboarding
   * funnel — every item that navigates away (account, credits, teams) is
   * stripped so users aren't pulled out of the flow
   * mid-onboarding.
   *
   * Pending team invitations are the one exception: they survive the focused
   * chrome. A user who lands in onboarding while an invite is waiting is
   * usually there BY MISTAKE (a team collaborator whose team project is
   * invisible to them), and the invite is their only way out. It costs the
   * ordinary new signup nothing — the block renders only when an invite
   * actually exists.
   */ menu?: 'full' | 'focused';
}
/**
 * Account dropdown component for the header.
 *
 * For anonymous users: Shows "Guest" label and triggers sign-up modal on click.
 * For signed-in users: Shows dropdown with identity, credits, teams, language, and sign-out.
 */ export function AccountDropdown({ alias, color, email, isAnonymous, onSignUpClick, variant = 'default', menu = 'full' }: AccountDropdownProps) {
    const isFocusedMenu = menu === 'focused';
    const t = useTranslations$1();
    const [showDropdown, setShowDropdown] = useState(false);
    const [showTeamInvitations, setShowTeamInvitations] = useState(false);
    const [showBalance, setShowBalance] = useState(false);
    const [isSigningOut, setIsSigningOut] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { billingInfo, refresh: refreshBillingInfo } = useBillingInfo();
    const { memberships } = useMemberships();
    const isInDesktopApp = useSyncExternalStore(()=>()=>{}, isDesktopApp, ()=>false);
    const teamOrgs = memberships?.teamOrgs ?? billingInfo?.teamOrgs ?? [];
    const billingTeamById = new Map((billingInfo?.teamOrgs ?? []).map((team)=>[
            team.id,
            team
        ]));
    const { invitations: pendingInvitations, isLoading: pendingInvitationsLoading, error: pendingInvitationsError, refresh: refreshPendingInvitations } = usePendingInvitations({
        enabled: showTeamInvitations && !isAnonymous
    });
    const close = useCallback(()=>{
        setShowDropdown(false);
        setShowTeamInvitations(false);
    }, []);
    const handleDropdownToggle = ()=>{
        const newState = !showDropdown;
        setShowDropdown(newState);
        if (!newState) setShowTeamInvitations(false);
        if (newState) trackAccountDropdownOpened();
    };
    // The balance opens a popover in place of the menu (top up without leaving the canvas).
    const handleBalanceClick = ()=>{
        close();
        trackAccountDropdownBalanceClicked({
            currentBalance: billingInfo?.personalOrg?.balance ?? 0
        });
        setShowBalance(true);
    };
    // Close dropdown when clicking outside
    useEffect(()=>{
        if (!showDropdown) return;
        const handleClickOutside = (e: MouseEvent)=>{
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                close();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return ()=>document.removeEventListener('mousedown', handleClickOutside);
    }, [
        showDropdown,
        close
    ]);
    const handleSignOut = async ()=>{
        setIsSigningOut(true);
        try {
            await signOut();
            window.location.href = '/';
        } catch  {
            setIsSigningOut(false);
        }
    };
    if (isAnonymous) {
        return (<button onClick={onSignUpClick} // In the rail the trigger is the avatar alone; the alias and Guest badge would overflow 48px.
        aria-label={variant === 'rail' ? `${alias} · ${t("zF7Cth")}` : undefined} className={cn(triggerClass, variant === 'outlined' && 'h-9 px-3 border border-border', variant === 'rail' && 'h-8 w-8 justify-center rounded-lg px-0', variant === 'row' && 'h-9 w-full gap-2.5 rounded-lg px-2')}>
        <div className="w-5 h-5 rounded-full flex-shrink-0" style={{
            backgroundColor: color
        }}/>
        {variant !== 'rail' && (<>
            <span className="text-foreground hidden sm:inline">{alias}</span>
            <span className="hidden sm:inline px-1.5 py-0.5 text-xs font-medium rounded bg-muted text-muted-foreground">
              {t("zF7Cth")}
            </span>
          </>)}
      </button>);
    }
    const hasTeams = teamOrgs.length > 0;
    const hasPendingInvites = billingInfo?.pendingInvitationsCount != null && billingInfo.pendingInvitationsCount > 0;
    const avatarOnly = variant === 'avatar' || variant === 'rail';
    const sidebar = variant === 'row' || variant === 'rail';
    return (<div className="relative" ref={dropdownRef}>
      <button onClick={handleDropdownToggle} className={cn(triggerClass, variant === 'outlined' && 'h-9 px-3 border border-border', variant === 'avatar' && 'h-8 w-8 justify-center px-0', variant === 'rail' && 'h-8 w-8 justify-center rounded-lg px-0', variant === 'row' && 'h-9 w-full gap-2.5 rounded-lg px-2')} title={avatarOnly ? alias : undefined} aria-label={alias} aria-expanded={showDropdown}>
        <div className={cn('flex flex-shrink-0 items-center justify-center rounded-full font-medium leading-none text-white', variant === 'avatar' ? 'h-6 w-6 text-xs' : 'h-5 w-5 text-[11px]')} style={{
        backgroundColor: color
    }}>
          {avatarOnly || variant === 'row' ? alias.charAt(0).toUpperCase() : null}
        </div>
        {!avatarOnly && (<>
            <span className={cn('text-foreground hidden sm:inline', variant === 'row' && 'min-w-0 flex-1 truncate text-start')}>
              {alias}
            </span>
            { /* No plan badge — plan is a per-workspace fact (billing shows it);
                the navbar account chip stays quiet. */ }
            <ChevronDown className={cn('w-3.5 h-3.5 text-muted-foreground transition-transform', showDropdown && 'rotate-180')}/>
          </>)}
      </button>

      {showDropdown && (<div className={cn('absolute w-56 bg-popover border border-border rounded-lg shadow-lg z-[100] overflow-hidden divide-y divide-border', sidebar ? 'start-0 bottom-full mb-1.5' : 'end-0 top-full mt-1.5')}>
          { /* Identity */ }
          {isFocusedMenu ? (<div className="block px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{alias}</p>
                {email && <p className="text-xs text-muted-foreground truncate">{email}</p>}
              </div>
            </div>) : (<Link href="/settings/account" onClick={close} className="block px-3 py-2 hover:bg-secondary/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{alias}</p>
                  {email && <p className="text-xs text-muted-foreground truncate">{email}</p>}
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 flex-shrink-0 ms-2"/>
              </div>
            </Link>)}

          { /* Pending team invitations — shown in the focused onboarding chrome
              too: accepting routes to /team/<slug>/welcome, which is the
              destination the user actually wanted. See the `menu` prop doc. */ }
          {hasPendingInvites && (<div>
              <button type="button" onClick={()=>setShowTeamInvitations((v)=>!v)} className={cn(itemClass, 'justify-between text-primary')} aria-expanded={showTeamInvitations}>
                <span className="flex items-center gap-2">
                  <Mail className="w-4 h-4"/>
                  {t("hfPjpW")}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-primary text-primary-foreground rounded-full">
                    {billingInfo!.pendingInvitationsCount}
                  </span>
                  <ChevronRight className={cn('w-3.5 h-3.5 transition-transform', showTeamInvitations && 'rotate-90')}/>
                </span>
              </button>
              {showTeamInvitations && (<PendingTeamInvitationsPanel invitations={pendingInvitations} isLoading={pendingInvitationsLoading} error={pendingInvitationsError} onRefresh={refreshPendingInvitations} onClose={close}/>)}
            </div>)}

          { /* Your credits */ }
          {!isFocusedMenu && billingInfo?.billingEnabled && (<button type="button" onClick={handleBalanceClick} className={cn(itemClass, 'justify-between')}>
              <span className="flex items-center gap-2">
                <OliveIcon className="w-4 h-4"/>
                {t("RZCgYV")}
              </span>
              <span className="font-mono text-xs">
                {billingInfo.personalOrg?.balance != null ? formatOlives(billingInfo.personalOrg.balance, {
        floor: true
    }) : '—'}
              </span>
            </button>)}

          { /* Teams */ }
          {!isFocusedMenu && hasTeams && (<div>
              {teamOrgs.map((team)=>{
        const billingTeam = billingTeamById.get(team.id);
        return (team.slug && (<Link key={team.id} href={`/team/${team.slug}`} onClick={close} className={cn(itemClass, 'justify-between')}>
                      <span className="flex items-center gap-2 min-w-0">
                        <Users className="w-4 h-4 flex-shrink-0"/>
                        <span className="truncate">{team.name}</span>
                      </span>
                      {billingTeam?.balance != null && (<span className="font-mono text-xs flex items-center gap-1 flex-shrink-0">
                          <OliveIcon className="w-3 h-3"/>
                          {formatOlives(billingTeam.balance, {
            floor: true
        })}
                        </span>)}
                    </Link>));
    })}
            </div>)}

          { /* Invite a friend, Get desktop app and Discord are sidebar rows on the home
              pages; the editor has no sidebar, so its avatar menu keeps them. */ }
          {!sidebar && !isFocusedMenu && billingInfo?.billingEnabled && (<Link href="/invites" onClick={close} className={itemClass}>
              <Gift className="w-4 h-4"/>
              {t("kMbI1q")}
            </Link>)}
          {!sidebar && !isFocusedMenu && !isInDesktopApp && (<Link href="/download" target="_blank" rel="noopener" onClick={close} className={itemClass}>
              <Monitor className="w-4 h-4"/>
              {t("VH2ERZ")}
            </Link>)}
          {!sidebar && !isFocusedMenu && (<TrackedDiscordLink source="top_bar" onClick={close} className={itemClass}>
              <DiscordIcon className="w-4 h-4"/>
              {t("YK1vHB")}
            </TrackedDiscordLink>)}

          { /* Language */ }
          <LocaleSwitcher variant="menu"/>

          { /* Log out */ }
          <button onClick={()=>{
        close();
        handleSignOut();
    }} disabled={isSigningOut} className={cn(itemClass, 'disabled:opacity-50')}>
            {isSigningOut ? <Loader2 className="w-4 h-4 animate-spin"/> : <LogOut className="w-4 h-4"/>}
            {t("PlBReU")}
          </button>
        </div>)}

      {billingInfo && (<OliveBalancePopover open={showBalance} onOpenChange={setShowBalance} anchorRef={dropdownRef} billingInfo={billingInfo} onRefresh={()=>void refreshBillingInfo()}/>)}
    </div>);
}
